import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Users, Percent, Plus, Trash2, RefreshCw, Loader2, 
  CheckCircle, Edit2, DollarSign, TrendingUp
} from 'lucide-react';

export default function GroupDiscountsTab() {
  const [loading, setLoading] = useState(true);
  const [discounts, setDiscounts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    min_guests: '',
    discount_type: 'percent', // 'percent' or 'flat'
    discount_percent: '',
    discount_amount: '',
    is_active: true
  });

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/discounts/group');
      setDiscounts(res.data || []);
    } catch (error) {
      console.error('Error loading discounts:', error);
      toast.error('Failed to load group discounts');
    } finally {
      setLoading(false);
    }
  };

  const saveDiscount = async () => {
    if (!formData.min_guests || (!formData.discount_percent && !formData.discount_amount)) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/admin/discounts/group', {
        min_guests: parseInt(formData.min_guests),
        discount_type: formData.discount_type,
        discount_percent: formData.discount_type === 'percent' ? parseFloat(formData.discount_percent) : 0,
        discount_amount: formData.discount_type === 'flat' ? parseFloat(formData.discount_amount) : 0,
        is_active: formData.is_active
      });
      
      toast.success('Group discount saved!');
      setShowDialog(false);
      setFormData({ min_guests: '', discount_type: 'percent', discount_percent: '', discount_amount: '', is_active: true });
      loadDiscounts();
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error('Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!confirm('Are you sure you want to delete this discount?')) return;
    
    try {
      await api.delete(`/admin/discounts/group/${discountId}`);
      toast.success('Discount deleted');
      loadDiscounts();
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast.error('Failed to delete discount');
    }
  };

  const openCreateDialog = () => {
    setEditingDiscount(null);
    setFormData({ min_guests: '', discount_percent: '', is_active: true });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="group-discounts-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-purple-500" />
            Group Discounts
          </h2>
          <p className="text-muted-foreground">Automatic discounts for larger groups</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadDiscounts} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Discount
          </Button>
        </div>
      </div>

      {/* How it Works */}
      <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-purple-600" />
          How Group Discounts Work
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">1</div>
            <div>
              <p className="font-medium">Set Thresholds</p>
              <p className="text-muted-foreground">Define minimum guest counts for each discount tier</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">2</div>
            <div>
              <p className="font-medium">Automatic Application</p>
              <p className="text-muted-foreground">Discounts apply automatically at checkout</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">3</div>
            <div>
              <p className="font-medium">Encourage Larger Groups</p>
              <p className="text-muted-foreground">Customers see potential savings, encouraging bigger bookings</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Discount Tiers */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4">Discount Tiers</h3>
        
        {discounts.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-lg font-medium text-muted-foreground">No group discounts configured</p>
            <p className="text-sm text-muted-foreground mb-4">Create discount tiers to encourage larger group bookings</p>
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Create First Discount
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {discounts.sort((a, b) => a.min_guests - b.min_guests).map((discount, idx) => (
              <div 
                key={discount.id || idx}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  discount.is_active ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{discount.min_guests}+</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-green-600">
                        {discount.discount_type === 'flat' 
                          ? `$${discount.discount_amount} OFF`
                          : `${discount.discount_percent}% OFF`
                        }
                      </span>
                      <Badge variant={discount.is_active ? 'default' : 'secondary'}>
                        {discount.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">
                      When booking for {discount.min_guests} or more guests
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteDiscount(discount.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Preview */}
      {discounts.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">Customer View Preview</h3>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Group Discounts Available!
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {discounts.filter(d => d.is_active).sort((a, b) => a.min_guests - b.min_guests).map((discount, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 text-center border border-green-200">
                  <p className="text-2xl font-bold text-green-600">{discount.discount_percent}%</p>
                  <p className="text-sm text-muted-foreground">{discount.min_guests}+ guests</p>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              {editingDiscount ? 'Edit' : 'Create'} Group Discount
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Minimum Guests</Label>
              <Input
                type="number"
                min="2"
                placeholder="e.g., 5"
                value={formData.min_guests}
                onChange={(e) => setFormData({ ...formData, min_guests: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Discount applies when this many or more guests are booked
              </p>
            </div>
            
            <div>
              <Label>Discount Type</Label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                className="w-full border rounded-md p-2 mt-1"
              >
                <option value="percent">Percentage (%)</option>
                <option value="flat">Flat Amount ($)</option>
              </select>
            </div>
            
            {formData.discount_type === 'percent' ? (
              <div>
                <Label>Discount Percentage</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    placeholder="e.g., 10"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
              </div>
            ) : (
              <div>
                <Label>Discount Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g., 50"
                    value={formData.discount_amount}
                    onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                    className="pl-8"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Fixed amount off per booking
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Enable this discount tier</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveDiscount} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
